import type { ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { ArrowLeft, ArrowRight, Star } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useGsap } from '@/hooks/use-gsap'
import { gsap, registerMotion, prefersReducedMotion } from '@/lib/motion'
import { SplitWords } from '@/components/generals/split-words'
import { communityAvatars, testimonials } from '@/constants/home'

const AUTOPLAY_MS = 6500

export const Community = (): ReactNode => {
    const [active, setActive] = useState(0)
    const [paused, setPaused] = useState(false)
    const slide = useRef<HTMLDivElement | null>(null)
    const previous = useRef(0)

    const scope = useGsap<HTMLElement>(({ gsap: g, select, scope: root, reduced }) => {
        if (reduced) return

        g.from(select('[data-community-eyebrow]'), {
            y: 18,
            opacity: 0,
            duration: 0.8,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        g.from(select('[data-community-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.04,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        // The face cluster pops in one tile at a time.
        g.from(select('[data-community-face]'), {
            scale: 0.55,
            opacity: 0,
            duration: 0.75,
            stagger: 0.07,
            ease: 'back.out(1.7)',
            scrollTrigger: { trigger: select('[data-community-faces]')[0], start: 'top 88%' }
        })

        g.from(select('[data-community-stage]'), {
            y: 44,
            opacity: 0,
            duration: 1,
            ease: 'nuvea',
            scrollTrigger: { trigger: select('[data-community-stage]')[0], start: 'top 88%' }
        })
    })

    // Cross-dissolve between quotes, sliding in the direction of travel.
    useEffect(() => {
        const node = slide.current

        if (!node) return

        registerMotion()

        if (prefersReducedMotion()) return

        const forward = active >= previous.current

        previous.current = active

        const tween = gsap.fromTo(
            node,
            { opacity: 0, y: forward ? 26 : -26 },
            { opacity: 1, y: 0, duration: 0.7, ease: 'nuvea', overwrite: true }
        )

        return () => {
            tween.kill()
        }
    }, [active])

    const go = useCallback((direction: 1 | -1) => {
        setActive((current) => (current + direction + testimonials.length) % testimonials.length)
    }, [])

    useEffect(() => {
        if (paused) return

        const timer = window.setInterval(() => {
            setActive((current) => (current + 1) % testimonials.length)
        }, AUTOPLAY_MS)

        return () => window.clearInterval(timer)
    }, [paused])

    const current = testimonials[active]

    return (
        <section
            ref={scope}
            id='community'
            className='bg-sage py-24 md:py-32'
        >
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <div className='flex flex-col items-center text-center'>
                    <p
                        data-community-eyebrow=''
                        className='type-micro flex items-center gap-2 rounded-full border border-stone-line bg-shell px-4 py-2 text-ink'
                    >
                        <Star className='size-3 fill-ink text-ink' strokeWidth={0} aria-hidden='true' />
                        3,100+ verified reviews
                    </p>

                    <SplitWords
                        data-community-heading=''
                        as='h2'
                        lines={['The people who', 'stopped buying more,', 'and started finishing.']}
                        className='type-h1 mt-7 max-w-[20ch] text-ink'
                    />

                    <ul data-community-faces='' className='mt-8 flex items-center'>
                        {communityAvatars.map((avatar, index) => (
                            <li
                                key={avatar}
                                data-community-face=''
                                className='relative size-12 overflow-hidden rounded-full border-2 border-cream not-first:-ml-4 md:size-14'
                                style={{ zIndex: communityAvatars.length - index }}
                            >
                                <Image
                                    src={avatar}
                                    alt=''
                                    aria-hidden='true'
                                    fill
                                    sizes='56px'
                                    className='object-cover'
                                />
                            </li>
                        ))}
                    </ul>
                </div>

                <div
                    data-community-stage=''
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocusCapture={() => setPaused(true)}
                    onBlurCapture={() => setPaused(false)}
                    className='mt-14 rounded-2xl border border-stone-line bg-shell p-8 md:mt-20 md:p-14'
                >
                    <div
                        className='min-h-[220px] md:min-h-[200px]'
                        aria-live='polite'
                        aria-atomic='true'
                    >
                        <div ref={slide} className='flex flex-col items-center text-center'>
                            <blockquote className='type-lead max-w-[46ch] text-ink'>
                                &ldquo;{current.quote}&rdquo;
                            </blockquote>

                            <div className='mt-8 flex items-center gap-3'>
                                <span className='relative size-10 overflow-hidden rounded-full'>
                                    <Image
                                        src={current.avatar}
                                        alt=''
                                        aria-hidden='true'
                                        fill
                                        sizes='40px'
                                        className='object-cover'
                                    />
                                </span>

                                <div className='text-left'>
                                    <p className='type-small text-ink'>
                                        {current.name}
                                    </p>

                                    <p className='type-micro text-slate-mute'>
                                        {current.context}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='mt-10 flex items-center justify-between gap-6'>
                        <div className='flex items-center gap-2'>
                            {testimonials.map((item, index) => (
                                <button
                                    key={item.id}
                                    type='button'
                                    onClick={() => setActive(index)}
                                    aria-label={`Show the review from ${item.name}`}
                                    aria-current={index === active ? 'true' : undefined}
                                    className={cn(
                                        'h-1 cursor-pointer rounded-full transition-[width,background-color] duration-500 ease-nuvea',
                                        index === active ? 'w-8 bg-ink' : 'w-3 bg-stone-line hover:bg-slate-mute'
                                    )}
                                />
                            ))}
                        </div>

                        <div className='flex items-center gap-2'>
                            <button
                                type='button'
                                onClick={() => go(-1)}
                                aria-label='Previous review'
                                className='flex size-10 cursor-pointer items-center justify-center rounded-full border border-stone-line text-ink transition-colors duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                            >
                                <ArrowLeft className='size-4' strokeWidth={1.6} />
                            </button>

                            <button
                                type='button'
                                onClick={() => go(1)}
                                aria-label='Next review'
                                className='flex size-10 cursor-pointer items-center justify-center rounded-full border border-stone-line text-ink transition-colors duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                            >
                                <ArrowRight className='size-4' strokeWidth={1.6} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
